import React, { useState, useCallback } from "react";

/**
 * @typedef {{ offerId: string, publicOfferUrl: string | null, draftStatus?: string }} FlightQuoteCreateSuccess
 */

export default function FlightDecisionCta({
  pricePrimary,
  /** כותרת מעל המחיר הראשי — ברירת מחדל לטיסות קלאסיות */
  pricePrimaryLabel = "מחיר כולל להמשך",
  priceSecondary,
  onContinue,
  /** When false, skip «להמשך להזמנה» — wizard footer is the single continue. */
  showContinueButton = true,
  onCreateQuote,
  disabled,
  creatingQuote = false,
  showPriceBlock = true,
  /** אחרי יצירת הצעה מוצלחת — ה-URL ללקוח (מקור: תגובת bookFlightOnly / public_offer_link) */
  quoteSuccess: quoteSuccessProp = null,
  onClearQuoteSuccess,
}) {
  const quoteSuccess = quoteSuccessProp;
  const [copyState, setCopyState] = useState("idle");
  const [showUrlFallback, setShowUrlFallback] = useState(false);

  const publicUrl = quoteSuccess?.publicOfferUrl
    ? String(quoteSuccess.publicOfferUrl).trim()
    : "";

  const copyLink = useCallback(async () => {
    if (!publicUrl) return;
    setShowUrlFallback(false);
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopyState("copied");
      setTimeout(() => {
        setCopyState("idle");
      }, 4000);
    } catch {
      setCopyState("fail");
      setShowUrlFallback(true);
    }
  }, [publicUrl]);

  const openOffer = useCallback(() => {
    if (!publicUrl) return;
    try {
      window.open(publicUrl, "_blank", "noopener,noreferrer");
    } catch {
      /* ignore */
    }
  }, [publicUrl]);

  const onClear = useCallback(() => {
    if (typeof onClearQuoteSuccess === "function") {
      onClearQuoteSuccess();
    }
    setCopyState("idle");
    setShowUrlFallback(false);
  }, [onClearQuoteSuccess]);

  return (
    <section
      className="fd-card fd-cta"
      aria-label={showContinueButton ? "המשך להזמנה" : "פעולות הצעת מחיר"}
      dir="rtl"
    >
      {showPriceBlock ? (
        <>
          <p className="fd-cta__price-label">{pricePrimaryLabel}</p>
          {pricePrimary ? (
            <p className="fd-cta__price">{pricePrimary}</p>
          ) : (
            <p className="fd-cta__price">—</p>
          )}
          {priceSecondary ? (
            <p className="fd-cta__sub">{priceSecondary}</p>
          ) : null}
        </>
      ) : null}
      {showContinueButton ? (
        <button
          type="button"
          className="fd-cta__btn"
          onClick={onContinue}
          disabled={disabled}
        >
          להמשך להזמנה
        </button>
      ) : null}

      {quoteSuccess ? (
        <div
          className="fd-cta__quote-success"
          role="region"
          aria-label="ההצעה נוצרה"
        >
          <p className="fd-cta__quote-success-title">ההצעה נוצרה</p>
          <p className="fd-cta__quote-success-hint">
            שיתוף מיידי ללקוח — בלי לעזוב את מסך פרטי הטיסה
          </p>
          {publicUrl ? (
            <>
              <button
                type="button"
                className="fd-cta__btn"
                onClick={copyLink}
                disabled={!publicUrl}
              >
                {copyState === "copied" ? "הקישור הועתק" : "העתק קישור"}
              </button>
              {copyState === "fail" && (
                <p className="fd-cta__copy-fail" role="status">
                  לא ניתן להעתיק אוטומטית — בחרו את הקישור:
                </p>
              )}
              {showUrlFallback && publicUrl && (
                <input
                  type="text"
                  readOnly
                  className="fd-cta__url-fallback-input"
                  value={publicUrl}
                  onFocus={(e) => e.target.select()}
                  aria-label="קישור להצעה"
                />
              )}
              <button
                type="button"
                className="fd-cta__btn fd-cta__btn--secondary"
                onClick={openOffer}
                disabled={!publicUrl}
              >
                פתח הצעה
              </button>
            </>
          ) : (
            <p className="fd-cta__quote-success-hint" role="status">
              הטיוטה נשמרה. אם לא מוצג קישור, בדקו בטיוטות או צרו איתנו קשר.
            </p>
          )}
          <button
            type="button"
            className="fd-cta__ghost-link"
            onClick={onClear}
          >
            יצירת הצעה נוספת
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="fd-cta__btn fd-cta__btn--secondary"
          onClick={onCreateQuote}
          disabled={creatingQuote || typeof onCreateQuote !== "function"}
        >
          {creatingQuote ? "יוצר טיוטה..." : "יצירת הצעת מחיר"}
        </button>
      )}

      <p className="fd-cta__trust">המחיר יעודכן סופית לפני התשלום</p>
    </section>
  );
}
